/**
 * PASTE A KEY, GET A CONNECTION — which provider an API key belongs to, from
 * the key alone.
 *
 * Every vendor Tavsin supports stamps its keys with a prefix, so asking a user
 * to pick "Anthropic" from a list and THEN paste `sk-ant-…` asks them to say
 * the same thing twice — and picking the wrong row is the commonest way a key
 * "doesn't work". This reads the key instead. It also accepts what people
 * actually paste: a line out of a `.env` file (`ANTHROPIC_API_KEY=sk-ant-…`),
 * a value in quotes, a `Bearer ` header, or a local server's URL.
 *
 * Pure and dependency-free: the main process uses it to configure, and both
 * renderers use it to say "Looks like an Anthropic key" while the user types.
 * Detection only names a provider; whether the key WORKS is answered by a real
 * one-token call (`providers.connect`), never by the shape.
 */

export type DetectedProvider = "anthropic" | "openai" | "gemini" | "openrouter" | "local";

export type KeyDetection =
  | { readonly ok: true; readonly provider: DetectedProvider; readonly label: string; readonly apiKey?: string; readonly baseUrl?: string }
  | { readonly ok: false; readonly reason: string };

export const DETECTED_LABELS: Readonly<Record<DetectedProvider, string>> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  gemini: "Google Gemini",
  openrouter: "OpenRouter",
  local: "a local server",
};

/** `.env` variable names that say which vendor a value is for. */
const ENV_HINTS: ReadonlyArray<readonly [RegExp, DetectedProvider]> = [
  [/^(ANTHROPIC|CLAUDE)_/i, "anthropic"],
  [/^OPENROUTER_/i, "openrouter"],
  [/^OPENAI_/i, "openai"],
  [/^(GEMINI|GOOGLE|GOOGLE_GENERATIVE_AI)_/i, "gemini"],
];

/** Strip what surrounds a key when it is copied from somewhere: an env line, quotes, a header. */
export function cleanPastedKey(raw: string): { value: string; envName: string | null } {
  let s = raw.trim();
  let envName: string | null = null;
  const env = /^(?:export\s+)?([A-Za-z_][A-Za-z0-9_]*)\s*[=:]\s*(.+)$/.exec(s);
  if (env && !/^https?:\/\//i.test(s)) {
    envName = env[1]!;
    s = env[2]!.trim();
  }
  s = s.replace(/^["'`]|["'`]$/g, "").trim();
  s = s.replace(/^Bearer\s+/i, "").trim();
  return { value: s, envName };
}

export function detectApiKey(raw: string): KeyDetection {
  const { value, envName } = cleanPastedKey(raw);
  if (value === "") return { ok: false, reason: "Paste an API key, or the address of a local model server." };
  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      return { ok: true, provider: "local", label: DETECTED_LABELS.local, baseUrl: url.toString().replace(/\/$/, "") };
    } catch {
      return { ok: false, reason: "That address is not a valid URL." };
    }
  }
  if (/\s/.test(value)) return { ok: false, reason: "A key has no spaces in it. Paste the key on its own." };
  const ok = (provider: DetectedProvider): KeyDetection => ({ ok: true, provider, label: DETECTED_LABELS[provider], apiKey: value });
  // Most specific prefixes first: `sk-ant-` and `sk-or-` are also `sk-`.
  if (/^sk-ant-[A-Za-z0-9_-]{20,}$/.test(value)) return ok("anthropic");
  if (/^sk-or-[A-Za-z0-9_-]{20,}$/.test(value)) return ok("openrouter");
  if (/^AIza[0-9A-Za-z_-]{30,}$/.test(value)) return ok("gemini");
  if (/^sk-[A-Za-z0-9_-]{20,}$/.test(value)) return ok("openai");
  // No prefix we know: trust a variable name from a pasted `.env` line.
  const hinted = envName ? ENV_HINTS.find(([re]) => re.test(envName))?.[1] : undefined;
  if (hinted && hinted !== "local" && value.length >= 20) return ok(hinted);
  return { ok: false, reason: "We couldn't tell which provider this key is for. Choose the provider under More options." };
}
